/**
 * @file scheduledJobFeature.tsx
 *
 * @fileoverview Exports the scheduled job feature related utils.
 */

import dynamic from 'next/dynamic';
import { IntlShape } from 'react-intl';
import { EntityFormProps } from '~/jobiqo/components/Form/EntityForm';
import {
  FIELD_JOB_DURATION,
  FIELD_JOB_EXPIRATION_DATE,
} from '~/jobiqo/components/Job/Form/components/DurationInput';
import {
  DEFAULT_JOB_PUBLISHING_DATE,
  FIELD_JOB_PUBLISHING_DATE,
} from '~/jobiqo/components/Job/Form/components/PublishingDateInput';
import * as Yup from 'yup';
import { Maybe } from '~/jobiqo/types/types';
import { useState } from 'react';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { useConfig } from '~/jobiqo/context/config';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import parseISO from 'date-fns/parseISO';

/**
 * Calculates the job duration based on publishing and expiration dates.
 * @param entity - The job entity
 * @param maximumJobDuration - The default maximum job duration
 * @param extendedRuntime - The extended runtime (if any)
 * @returns An object containing maxAllowedDuration and defaultDuration
 */
function calculateJobDurations(
  entity: any,
  maximumJobDuration: number,
  extendedRuntime: Maybe<number> = 0,
): {
  maxAllowedDuration: number;
  defaultDuration: number;
} {
  let maxAllowedDuration = maximumJobDuration + Number(extendedRuntime);
  let defaultDuration = maximumJobDuration;

  const now = new Date();
  const publishingDateStr =
    entity?.[FIELD_JOB_PUBLISHING_DATE] || DEFAULT_JOB_PUBLISHING_DATE;
  const publishingDate = parseISO(publishingDateStr);

  // If we already have an expiration date, and the publishing date is in
  // the future, determine the duration based on those two dates.
  if (publishingDate > now && entity?.field_job_expiration_date) {
    const expirationDate = parseISO(entity.field_job_expiration_date);

    if (!isNaN(expirationDate.getTime()) && !isNaN(publishingDate.getTime())) {
      // Match the backend: each duration day is exactly 24 hours.
      const diffDays = Math.floor(
        (expirationDate.getTime() - publishingDate.getTime()) / 86400000,
      );
      defaultDuration = diffDays > 0 ? diffDays : 1;
      // Set the max allowed duration to the default duration in this case.
      maxAllowedDuration = defaultDuration;
    }
  }

  return {
    maxAllowedDuration,
    defaultDuration,
  };
}

/**
 * Returns the pre-filled value for the duration input field.
 *
 * A duration stored on the job is used when it is within maxAllowedDuration
 * (the maximum may have been lowered since the job was saved).
 *
 * Otherwise, when editing an unpublished job that already has an expiration date,
 * the duration is back-calculated as the difference in 24-hour days
 * between the expiration and publishing dates. If that value is valid
 * (positive and within maxAllowedDuration), it is used; otherwise the
 * function falls back to the system defaultDuration.
 *
 * @param entity - The job entity
 * @param isEditPage - Whether we are on the edit page
 * @param isJobPublished - Whether the job is published
 * @param defaultDuration - The default duration from calculateJobDurations
 * @param maxAllowedDuration - The maximum allowed duration
 * @returns The duration in days to pre-fill in the form
 */
export function getEffectiveDefaultDuration(
  entity: any,
  isEditPage: boolean,
  isJobPublished: boolean,
  defaultDuration: number,
  maxAllowedDuration: number,
): number {
  const storedDuration = Number(entity?.[FIELD_JOB_DURATION]);
  if (storedDuration > 0 && storedDuration <= maxAllowedDuration) {
    return storedDuration;
  }
  // Calculate the effective default duration only if we are on the edit page,
  // the job is not published, and there is an expiration date set.
  if (isEditPage && !isJobPublished && entity?.[FIELD_JOB_EXPIRATION_DATE]) {
    const entityExpDate = parseISO(entity[FIELD_JOB_EXPIRATION_DATE]);
    const pubDate = parseISO(
      entity?.[FIELD_JOB_PUBLISHING_DATE] || DEFAULT_JOB_PUBLISHING_DATE,
    );

    if (!isNaN(entityExpDate.getTime()) && !isNaN(pubDate.getTime())) {
      const diffDays = Math.floor(
        (entityExpDate.getTime() - pubDate.getTime()) / 86400000,
      );
      if (diffDays > 0 && diffDays <= maxAllowedDuration) {
        return diffDays;
      }
    }
  }
  return defaultDuration;
}

export { calculateJobDurations };

const JobFormPublishingDateInput = dynamic(
  import('~/jobiqo/components/Job/Form/components/PublishingDateInput'),
);
const JobFormDurationInput = dynamic(
  import('~/jobiqo/components/Job/Form/components/DurationInput'),
);

interface ShceduledJobFormProps {
  /**
   * The flag that indicates if we are on the job edit page.
   */
  isEditPage: boolean;
  /**
   * The flag that indicates if the job is published.
   */
  isJobPublished: boolean;
  /**
   * The flag that indicates if the job is paused.
   */
  isJobPaused: boolean;
  /**
   * The entity in the case of an edit form.
   */
  entity: any;
  /**
   * The flag that indicates if we are republishing a job.
   */
  republishing?: boolean;
  /**
   * The job board default runtime information (how much time a job ad is online).
   */
  maximumJobDuration: number;
  /**
   * The current product extended runtime (if any).
   *
   * @default "0"
   */
  extendedRuntime?: Maybe<number>;
}

// Returns the job EntityForm props for the scheduled job feature.
export function getSheduledJobFormProps({
  isEditPage,
  isJobPublished,
  isJobPaused,
  entity,
  republishing,
  maximumJobDuration,
  extendedRuntime = 0,
}: ShceduledJobFormProps): Pick<
  EntityFormProps,
  'additionalFieldList' | 'formGroupsChildren'
> {
  const scheduledJobFeatureActive = getCurrentFrontendConfig().JOBS
    .SCHEDULED_JOB_FEATURE_ACTIVE;
  const { getConfigByName } = useConfig();
  const JOB_END_DATE_PICKER: boolean = !!getConfigByName('job_end_date_picker')
    ?.value[0];

  if (!scheduledJobFeatureActive) {
    return {};
  }

  const { maxAllowedDuration, defaultDuration } = calculateJobDurations(
    entity,
    maximumJobDuration,
    extendedRuntime,
  );

  return {
    additionalFieldList: [FIELD_JOB_DURATION, FIELD_JOB_PUBLISHING_DATE],
    formGroupsChildren: {
      job_schedule: ({ definition }) => {
        const timeZone = definition?.fields
          .find((field) => field.id === FIELD_JOB_PUBLISHING_DATE)
          ?.attributes?.find((attr) => attr.key === 'default_timezone')?.value;

        const defaultValue = definition?.fields.find(
          (field) => field.id === FIELD_JOB_PUBLISHING_DATE,
        )?.defaultValue
          ? new Date(
              Number(
                definition.fields.find(
                  (field) => field.id === FIELD_JOB_PUBLISHING_DATE,
                ).defaultValue,
              ) * 1000,
            ).toLocaleString('en-US', { timeZone })
          : DEFAULT_JOB_PUBLISHING_DATE;

        const [defaultValueDate, _] = useState<Date | string>(defaultValue);

        const effectiveDefaultDuration = getEffectiveDefaultDuration(
          entity,
          isEditPage,
          isJobPublished,
          defaultDuration,
          maxAllowedDuration,
        );

        return (
          <>
            <JobFormPublishingDateInput
              isReadOnly={isEditPage && (isJobPublished || isJobPaused)}
              // If we are republishing we want to set the default date.
              value={
                republishing
                  ? defaultValueDate
                  : entity?.[FIELD_JOB_PUBLISHING_DATE]
              }
            />
            <Spacing size="medium" />
            {isEditPage && isJobPublished ? (
              <JobFormDurationInput
                publishedJob={{
                  expiration: entity[FIELD_JOB_EXPIRATION_DATE],
                  duration: entity[FIELD_JOB_DURATION],
                }}
                initialPublishingDate={entity?.[FIELD_JOB_PUBLISHING_DATE]}
                useDatePicker={JOB_END_DATE_PICKER}
              />
            ) : (
              <JobFormDurationInput
                key={`${FIELD_JOB_DURATION}-${maxAllowedDuration}-${effectiveDefaultDuration}`}
                maxValue={maxAllowedDuration}
                defaultValue={effectiveDefaultDuration}
                extendedRuntime={!!extendedRuntime}
                useDatePicker={JOB_END_DATE_PICKER}
                initialPublishingDate={entity?.[FIELD_JOB_PUBLISHING_DATE]}
              />
            )}
            <Spacing size="medium" />
          </>
        );
      },
    },
  };
}

/**
 * It returns a Yup validation schema for the job form
 * scheduling fields.
 * @param {IntlShape} intl - IntlShape
 * @param maximumJobDuration - The maximum number of days a job can be online.
 * @param extendedDuration - The allowed extended duration (if any).
 * @param entity - The job entity.
 * @param isPublishedEdit - Whether validation is for an already published job.
 * @returns An object with the Yup validation schema for the job scheduling fields.
 */
export function getJobSchedulingValidationSchema(
  intl: IntlShape,
  maximumJobDuration,
  extendedDuration = 0,
  entity,
  isPublishedEdit = false,
) {
  const scheduledJobFeatureActive = getCurrentFrontendConfig().JOBS
    .SCHEDULED_JOB_FEATURE_ACTIVE;
  const { FIELD_NUMBER } = getCommonTranslations();

  const { maxAllowedDuration } = calculateJobDurations(
    entity,
    maximumJobDuration,
    extendedDuration,
  );

  // Published jobs have job-specific limits, including already purchased upgrades.
  // The backend validates that limit and returns a formatted maximum-date error.
  if (scheduledJobFeatureActive && isPublishedEdit) {
    return {
      [FIELD_JOB_DURATION]: Yup.number().typeError(FIELD_NUMBER).min(1),
    };
  }
  return {
    ...(scheduledJobFeatureActive && {
      [FIELD_JOB_DURATION]: Yup.number()
        .typeError(FIELD_NUMBER)
        .max(
          maxAllowedDuration,
          intl.formatMessage(
            {
              id: 'job_duration_max_value',
              defaultMessage:
                'The maximum number of days online is {maxValue}.',
            },
            { maxValue: maxAllowedDuration },
          ),
        ),
    }),
  };
}
